import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SubjectIcon from '@mui/icons-material/Subject';
import { Box, Link, Stack, Typography } from '@mui/material';
import React, { memo, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { GroupSettingsContext } from '../../../../../contexts/GroupSettingsContext';
import { formatMetricValue } from '../../../../../shared/dashboard';
import theme from '../../../../../theme';
import { CustomFieldType } from '../../../../../types/common';
import { Dashboard } from '../../../../../types/files';
import CustomFieldModal from '../../../../modals/tables/dashboards/rows/CustomFieldModal';

const CustomFieldCell: React.FC<{
    dashboard: Dashboard,
    fieldName: string,
 }> = ({ dashboard, fieldName }) => {
    const { customFields: settingsCustomFields } = useContext(GroupSettingsContext);
    const [customFieldModalOpen, setCustomFieldModalOpen] = useState<boolean>(false);
    const [customFieldValues, setCustomFieldValues] = useState<string>('');
    const latestUpdateRef = useRef<HTMLElement>(null);

    const isMetric = useMemo(() => {
        const cf = dashboard?.customFields?.find(({ name }) => name === fieldName);
        if (cf?.type === CustomFieldType.Metric) return true;
        return settingsCustomFields.find(({ name }) => name === fieldName)?.type === CustomFieldType.Metric;
    }, [dashboard?.customFields, settingsCustomFields, fieldName]);

    useEffect(() => {
        const cf = dashboard?.customFields?.find(({ name }) => name === fieldName);
        if (!cf) { setCustomFieldValues(''); return; }
        if (isMetric) {
            if (!cf.metric?.value) { setCustomFieldValues(''); return; }
            const unit = cf.metric?.unit
                ?? (settingsCustomFields.find(({ name }) => name === fieldName)?.unit as string | undefined)
                ?? '#';
            setCustomFieldValues(formatMetricValue(cf.metric.value, unit));
        } else {
            setCustomFieldValues(cf.value as string);
        }
    }, [dashboard?.customFields, fieldName, isMetric, settingsCustomFields]);

    return (<>
        <Stack width="100%" ref={latestUpdateRef}>
            {(!!customFieldValues && !['N/A', 'None'].includes(customFieldValues)) ? (
                <Stack direction="row" alignItems="center" gap="8px" width="100%"
                    onClick={() => setCustomFieldModalOpen(true)}
                    sx={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '0 10px 0 0',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        '&:hover': { backgroundColor: '#f9fafb' },
                    }}>
                    <Box sx={{
                        width: 34, height: 34, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: '#e0f2f1',
                        borderRadius: '7px 0 0 7px',
                    }}>
                        {isMetric
                            ? <BarChartRoundedIcon sx={{ fontSize: 18, color: '#4aab96' }} />
                            : <SubjectIcon sx={{ fontSize: 18, color: '#4aab96' }} />}
                    </Box>
                    <Typography sx={{
                        fontFamily: 'Lato',
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        color: '#374151',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        minWidth: 0,
                    }}>
                        {customFieldValues}
                    </Typography>
                    <KeyboardArrowDownIcon sx={{ fontSize: 18, color: '#9ca3af', flexShrink: 0 }} />
                </Stack>
            ) : (
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                    <Link fontFamily="Lato" fontSize="0.9rem" fontWeight={500}
                        color={`${theme.colors.neutral['500']} !important`}
                        underline='hover'
                        onClick={() => setCustomFieldModalOpen(true)}
                        sx={{ cursor: 'pointer' }}>
                        {'Add value...'}
                    </Link>
                </Stack>
            )}
        </Stack>
        {customFieldModalOpen && (
            <CustomFieldModal
                open={customFieldModalOpen}
                dashboard={dashboard}
                fieldName={fieldName}
                newValue={!customFieldValues || ['N/A', 'None'].includes(customFieldValues)}
                anchorElement={latestUpdateRef.current}
                onClose={() => setCustomFieldModalOpen(false)} />
        )}
    </>);
}

export default memo(CustomFieldCell);
